import { useEffect, useState } from 'preact/hooks'
import { Globe, X } from 'lucide-preact'

const URL_MAX_LENGTH = 2048
const URL_HTTP_REGEX = /^https?:\/\/.+/i

interface Props {
    isOpen: boolean
    onClose: () => void
    onSubmit: (url: string) => Promise<void> | void
    title: string
    description: string
    submitLabel: string
    initialUrl?: string
    /** Optional warning rendered above the input (e.g. when switching modes). */
    warning?: string
}

export function ExternalUrlModal({ isOpen, onClose, onSubmit, title, description, submitLabel, initialUrl = '', warning }: Props) {
    const [url, setUrl] = useState(initialUrl)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (isOpen) {
            setUrl(initialUrl)
            setError(null)
        }
    }, [isOpen, initialUrl])

    if (!isOpen) return null

    const trimmed = url.trim()
    const validate = (v: string): string | null => {
        if (!v) return 'URL is required.'
        if (v.length > URL_MAX_LENGTH) return `URL must be ${URL_MAX_LENGTH} characters or fewer.`
        if (!URL_HTTP_REGEX.test(v)) return 'URL must start with http:// or https://.'
        return null
    }

    const handleSubmit = async () => {
        const v = trimmed
        const validationError = validate(v)
        if (validationError) {
            setError(validationError)
            return
        }
        setError(null)
        setSubmitting(true)
        try {
            await onSubmit(v)
        } finally {
            setSubmitting(false)
        }
    }

    const handleClose = () => {
        if (!submitting) onClose()
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={handleClose} />
            <div className="relative w-full max-w-md rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xl p-5 space-y-4">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                    <Globe className="w-5 h-5 text-primary-600" /> {title}
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-300">{description}</p>
                {warning && (
                    <p className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded p-2">
                        {warning}
                    </p>
                )}
                <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">External URL</label>
                    <input
                        type="url"
                        value={url}
                        maxLength={URL_MAX_LENGTH}
                        placeholder="https://example.com/your-landing-page"
                        onInput={(e) => setUrl((e.target as HTMLInputElement).value)}
                        className="w-full px-3 py-2 text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        disabled={submitting}
                    />
                    {error && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>}
                </div>
                <div className="flex items-center justify-end gap-2 pt-1">
                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={submitting}
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
                    >
                        <X className="w-4 h-4" /> Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={submitting || trimmed.length === 0}
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                    >
                        {submitting ? 'Saving…' : submitLabel}
                    </button>
                </div>
            </div>
        </div>
    )
}
